import { useEffect, useSyncExternalStore } from 'react'
import { loadProducts, productsVersion, subscribeProducts, visibleProductList, allProducts, canManageProducts, ProductDef } from '@/config/products'
import { platformSettingsService } from '@/services/platformSettingsService'
import { useAuth } from '@/hooks/useAuth'
import { getAllowedProducts } from '@/config/productAccess'

function useProductStore() {
  const { user } = useAuth()
  useSyncExternalStore(subscribeProducts, productsVersion, productsVersion)
  // Re-read whenever the signed-in account changes (sign-in, profile refresh
  // after the terms are accepted, a different user), not just on first mount.
  const account = user?.id ?? ''
  useEffect(() => {
    if (!account) return
    void loadProducts(() => platformSettingsService.listProducts(), true)
  }, [account])
}

/**
 * The products this site shows, kept current.
 *
 * Reads the owner's list from the auth service and re-renders the caller
 * whenever it changes, so the sidebar, header menu, home grid, Products page
 * and search never disagree.
 */
export function useVisibleProducts(): ProductDef[] {
  useProductStore()
  const { user } = useAuth()
  // Accounts provisioned for some products only (jshine_admin: JShine Camera
  // Health) see that subset of the shared list, in every view alike.
  const allowed = getAllowedProducts(user?.account)
  const list = visibleProductList()
  return allowed === null ? list : list.filter((p) => (allowed as string[]).includes(p.id))
}

/** Every product, hidden ones included, for the owner's settings page. */
export function useAllProducts(): ProductDef[] {
  useProductStore()
  return allProducts()
}

/** Whether the signed-in account may change the product list. */
export function useCanManageProducts(): boolean {
  useProductStore()
  return canManageProducts()
}
